/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { InstanceData } from "app/models/instance-data";
import { ReaderInstance } from "app/models/reader-instance";
import { ServiceInstance } from "app/models/service-instance";
import { DemoOrderValidationService, DemoOrderValidation_OrderLineDto, DemoOrderValidation_ValidationInfoDto, DemoOrderValidation_ValidationStateEnum } from "app/services/demo-order-validation.service";
import { KissReaderStateEnum } from "app/services/kiss.service";
import { ReaderService } from "app/services/reader.service";

/** The delay between the API calls to refresh the information in the UI */
const RefreshDelayInMs = 1000;

/** Property Key on the Demo Order Portal Validation Service's "Reader" Property which is used to specify the Reader that should be controlled. */
const ServicePropertyKey_Reader = "reader";

/** Property Key on Reader Instance's which is used for the Reader's Status. */
const ReaderPropertyKey_ReaderStatus = "reader_status";

/** Function Key for the "Start Reader" Function to be performed on the Reader */
const ReaderFunctionKey_StartReader = "start";

/** Function Key for the "Stop Reader" Function to be performed on the Reader */
const ReaderFunctionKey_StopReader = "stop";

/**
 * Page Component used in the Demo Order Validation Process.
 * 
 * This page is used to show the Order Validation Progress as items are
 * being read for the Order.  It has controls for the Reader at the bottom
 * and allows the User to process / clear the current Order in progress
 * as well as see how that progress is going. 
 */
@Component({
  templateUrl: './demo-order-validation.dashboard.html',
  styleUrls: ['./demo-order-validation.dashboard.css']
})
export class DemoOrderValidationDashboardComponent implements OnInit, OnDestroy {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

    /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  /** Portal Instance which is performing the Order Validation and whose information is being shown */
  portal: ServiceInstance;

  /** Current State being reflected in the UI */
  state: DemoOrderValidation_ValidationInfoDto;

  /** Flag used to indicate if the component has been destroyed or not.  This is used by the background "Refresh Data" task to indicate when it should stop. */
  isDestroyed = false;

  /** Reader that is being controlled by the Portal */
  reader: ReaderInstance = null;

  /** Holds the current Reader State if it is known */
  readerState: KissReaderStateEnum = null;

  /** Flag used to indicate if the Reader State has been retrieved or not. Used to disable some UI controls while the state is being retrieved.*/
  isReaderStateRetrieved: boolean = false;

  /** Flag used to indicate if any Reader or Order Actions are being performed.  This includes: [Start Reader, Stop Reader, Clear Order, Process Order] */
  isActionBeingPerformed: boolean = false;

  /** Constructor */
  constructor(
    private demoOrderValidationService: DemoOrderValidationService,
    private readerService: ReaderService,
    private route: ActivatedRoute,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  /**
   * Initializes the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnInit(): void {
    console.log(`ngOnInit()`);

    /* 
     * Get the data that was resolved in the background and kickstart the 
     * "Refresh" task that keeps the UI up to date
     */
    this.route.data.subscribe((
      data: {
        portal: ServiceInstance,
        state: DemoOrderValidation_ValidationInfoDto
      }
    ) => {
      // Update the portal / state values that were retrieved in the background
      this.portal = data?.portal;
      this.state = data?.state;

      // Validate that the Portal is in the expected state, if not forward the User to a different page.
      switch (this.state.state) {
        case DemoOrderValidation_ValidationStateEnum.EnterOrderInfo: {
          this.router.navigate(['order', 'portal-validation', this.portal.id, 'enter-info'], {});
          break;
        }
        case DemoOrderValidation_ValidationStateEnum.OrderBeingDisplayed: {
          // This is the desired state, just allow the User to continue.
          break;
        }
        default: {
          this.handleError({ error: `Unhandled State: '${this.state.state}'...`});
          this.router.navigate(['order', 'portal-validation', 'select-portal'], {});
          break;
        }
      }

      // Get the Reader that is configured to be controlled by the Portal
      let datum: InstanceData = this?.portal?.data?.find(datum => datum?.property?.property == ServicePropertyKey_Reader);
      this.reader = datum != null? datum.object_value.reader_reference : null;
    
      // Refresh the Reader State so that it is up to date
      this.refreshReaderState();

      // Start the background task to refresh the data being displayed
      setTimeout(this.refreshData.bind(this), RefreshDelayInMs);
    });
  }

  /**
   * Destroys the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  /**
   * Refreshes the UI Display.  Constantly called by background tasks every so often
   * to keep the UI up to date with the latest changes / reads. 
   */
  private async refreshData(): Promise<void> {
    // If the Component has been destroyed, then just return as there's nothing to do
    if (this.isDestroyed) {
      return;
    }

    // Call the API to get the current state of the Portal...
    let state: DemoOrderValidation_ValidationInfoDto = null;
    try {
      state = await this.demoOrderValidationService.getCurrentState(this.portal.id);
    } catch (error) {
      console.error("Probelm obtaining current state of Portal.", error);
    }

    // Update the current state if needed and tell angular that it needs to update the UI (needed since this happens in the background outside it's change detection)
    if (state != null && (JSON.stringify(this.state) != JSON.stringify(state))) {
      this.state = state;
      this.changeDetectorRef.detectChanges();
    }

    // If the component still hasn't been destroyed, then schedule the next refresh
    if (!this.isDestroyed) {
      setTimeout(this.refreshData.bind(this), RefreshDelayInMs);
    }
  }

  /**
   * Calls the API to re-obtain the reference to the Reader being controlled by the Portal.
   * This is done to obtain the "Reader State" Property Value so we can determine if the 
   * Reader has been started or not. 
   */
  private async refreshReaderState(): Promise<void> {
    // Obtain a fresh reference to the Reader using it's ID...
    let reader: ReaderInstance = null;
    try {
      reader = await this.readerService.getReaderInstanceById(this.reader?.id);
    } catch (error) {
      console.error("Problem obtaining Reader State.", error);
      return;
    }
    this.reader = reader;

    // Get the current state from the Reader Data for the "Reader Status" Property
    const datum = this.reader?.data?.find(datum => datum?.property?.property == ReaderPropertyKey_ReaderStatus);
    if (datum != null) {
      this.readerState = datum.value as KissReaderStateEnum;
    }

    // Update the Flag for whether the Reader State has been retrieved or not.
    this.isReaderStateRetrieved = true;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * being in error. (RED)
   */
  isErrorRow(lineItem: DemoOrderValidation_OrderLineDto): boolean {
    return lineItem?.current_quantity > lineItem?.expected_quantity && lineItem?.expected_quantity > 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighted as
   * being unknown. (GRAY)
   */
  isUnknownRow(lineItem: DemoOrderValidation_OrderLineDto): boolean {
    return lineItem?.expected_quantity == 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * being completed successfully. (GREEN)
   */
  isSuccessRow(lineItem: DemoOrderValidation_OrderLineDto): boolean {
    return lineItem?.current_quantity == lineItem?.expected_quantity;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * partially fulfilled. (YELLOW)
   */
  isPartialRow(lineItem: DemoOrderValidation_OrderLineDto): boolean {
    return lineItem?.current_quantity > 0 && lineItem?.expected_quantity > 0 && lineItem?.current_quantity < lineItem?.expected_quantity;
  }

  /**
   * Called by the UI when the "Start Reader" button is clicked.
   * Kicks off the "Start Reader" Action in the background if no Action
   * is currently being performed.
   */
  onStartReaderClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.startReader();
    }
  }

  /**
   * Performs the "Start Reader" Action
   */
  private async startReader(): Promise<void> {
    let result: any = null;
    try {
      result = await this.readerService.executeFunctionByKey(this.reader?.id, ReaderFunctionKey_StartReader, null);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }

    await this.refreshReaderState();
    this.isActionBeingPerformed = false;
  }

  /**
   * Called by the UI when the "Stop Reader" button is clicked.
   * Kicks off the "Stop Reader" Action in the background if no Action
   * is currently being performed.
   */
  onStopReaderClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.stopReader();
    }
  }

  /**
   * Performs the "Stop Reader" Action
   */
  private async stopReader(): Promise<void> {
    let result: any = null;
    try {
      result = await this.readerService.executeFunctionByKey(this.reader?.id, ReaderFunctionKey_StopReader, null);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }

    await this.refreshReaderState();
    this.isActionBeingPerformed = false;
  }

  /**
   * Called by the UI when the "Clear Order" button is clicked.
   * Kicks off the "Clear Order" Action in the background if no Action
   * is currently being performed.
   */
  onClearOrderClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.clearOrder();
    }
  }

  /**
   * Performs the "Clear Order" Action
   */
  private async clearOrder(): Promise<void> {
    let state: DemoOrderValidation_ValidationInfoDto = null;
    try {
      state = await this.demoOrderValidationService.clearOrder(this.portal.id);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }

    this.router.navigate(['order', 'portal-validation', this.portal.id, 'enter-info'], {});
    this.isActionBeingPerformed = false;
  }

  /**
   * Called by the UI when the "Process Order" button is clicked.
   * Kicks off the "Process Order" Action in the background if no Action
   * is currently being performed.
   */
  onProcessClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.processOrder();
    }
  }

  /**
   * Performs the "Process Order" Action
   */
  private async processOrder(): Promise<void> {
    try {
      await this.demoOrderValidationService.processOrder(this.portal.id);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }

    this.router.navigate(['order', 'portal-validation', this.portal.id, 'enter-info'], {});
    this.isActionBeingPerformed = false;
  }

  /**
   * Returns a boolean which indicates if the Reader being controlled by the Portal
   * is started or not.  Used by the UI to disable some controls.
   */
  isReaderStarted(): boolean {
    return this.isReaderStateRetrieved && this.readerState == KissReaderStateEnum.Started;
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}